import { useEffect } from "react";

import { SITE } from "../../data/site";

type SeoProps = {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: object | object[];
};

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]'
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Client-side SEO manager for a CRA app: keeps title, description,
 * canonical, Open Graph / Twitter tags, and JSON-LD in sync per route.
 */
const Seo = ({
  title,
  description,
  path,
  ogImage = SITE.ogImage,
  ogType = "website",
  jsonLd,
}: SeoProps) => {
  useEffect(() => {
    const url = `${SITE.url}${path}`;

    document.title = title;
    upsertMeta("name", "description", description);
    upsertCanonical(url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:secure_url", ogImage);
    upsertMeta("property", "og:image:type", "image/png");
    upsertMeta("property", "og:image:width", String(SITE.ogImageWidth));
    upsertMeta("property", "og:image:height", String(SITE.ogImageHeight));
    upsertMeta("property", "og:image:alt", SITE.ogImageAlt);
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:locale", "en_US");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "twitter:image:alt", SITE.ogImageAlt);

    const scriptId = "seo-jsonld";
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, path, ogImage, ogType, jsonLd]);

  return null;
};

export default Seo;
